import { anonymize as anonimizeFn } from './anonymize';

export type JSONObject = { [key: string]: any };
export type JSONArray = Array<JSONObject>;
export type JSONValue =
  | string
  | number
  | boolean
  | null
  | JSONObject
  | JSONArray;

export interface AnalyticsEvent {
  type: 'track';
  event: string;
  properties?: JSONValue;
  received_at?: Date;
}

export interface PageDefault {
  path: string;
  referrer?: string;
  search?: string;
  title?: string;
  url?: string;
  utm?: Record<string, string>;
}

export interface PageView {
  page: PageDefault;
  visit_start?: Date;
  visit_end?: Date;
}

export interface TrafficSource {
  type: 'utm' | 'referrer';
  value: string;
}

export interface AccountScore {
  fit_grade: number;
  fit_grade_letter: 'A' | 'B' | 'C' | 'D' | 'F';
}

export interface Raw {
  events?: AnalyticsEvent[];
  traits?: Record<string, JSONValue>;
  firmographics?: Record<string, JSONValue>;
  person?: Record<string, JSONValue>;
  pageViews?: PageView[];
  trafficSources?: TrafficSource[];
  accountScore?: AccountScore;
}

import {
  CountMinSketch,
  CountMinSketchOptions,
  JsBloom,
  JsBloomOptions,
} from '@getkoala/js-bloom';

export * from '@getkoala/js-bloom';

import { Company } from './company';
import { Events } from './events';
import { inSegment, when } from './in-segment';
import type { Indexable, Profile } from './indexing';
import { Page } from './page';
import { Person } from './person';
import { Traits } from './traits';

export type BuildOptions = {
  b?: JsBloomOptions;
  c?: CountMinSketchOptions;
};

import { index as rawIndex } from './indexing';
import { Scores } from './scores';
import { RawProfile } from './raw-profile';

export interface EdgeAPI {
  traits: Traits;
  events: Events;
  page: Page;
  company: Company;
  person: Person;
  scores: {
    account: Scores;
  };
  inSegment: typeof inSegment;
  when: typeof when;
  raw: Profile;
  index: (indexable: Indexable | Indexable[]) => void;
  rawProfile?: RawProfile
}

function index(profile: Profile, indexable: Indexable | Indexable[]): void {
  rawIndex(profile, indexable);
}

export function build(data: BuildOptions): EdgeAPI {
  const bloom = new JsBloom(data.b);
  const counts = new CountMinSketch(data.c);

  const profile: Profile = {
    counts,
    bloom,
  };

  return {
    traits: new Traits(profile),
    events: new Events(profile),
    page: new Page(profile),
    company: new Company(profile),
    person: new Person(profile),
    scores: {
      account: new Scores(profile, 'account_score'),
    },
    inSegment,
    when,
    raw: profile,
    index: (indexable: Indexable | Indexable[]) => index(profile, indexable),
  };
}

// Useful for testing
export function anonymize(raw: Raw): Profile {
  return anonimizeFn(raw)
}
